import React, { useState, useEffect } from "react";
import axios from "axios";
import cummonStyle from "../SettingModal.module.scss";
import styles from "./NotificationSettings.module.scss";
import FormInputText from "../Forms/FormInputText";
import { PageLoader } from "../../Loaders/PageLoader";
import { getImageUrl } from "../../../../commons/assets_path";
import { useTranslation } from "react-i18next";

import type { NoticeType } from "../../../../types/settingsTypes";

axios.defaults.headers.common["X-Requested-With"] = "XMLHttpRequest";

type Props = {
  client: {
    code: string;
    id: number;
  };
  client_member_id: number;
  service_icon: {
    slack: string;
    chatwork: string;
  };
  setNotice: React.Dispatch<React.SetStateAction<NoticeType>>;
  changeModalCss?: (width: string, height: string) => void;
  setIsMenuOpen?: (isOpen: boolean) => void;
  setHeaderShow?: (show: boolean) => void;
};

type Mode = "default" | "chatwork" | "unlink";

const NotificationSettings = ({
  client,
  client_member_id,
  service_icon,
  setNotice,
  changeModalCss,
  setIsMenuOpen,
  setHeaderShow,
}: Props) => {
  const { t } = useTranslation();

  const [loading, setLoading] = useState(true);
  const [mode, setMode] = useState<Mode>("default");
  const [slackData, setSlackData] = useState<{
    id: string;
    is_enabled: boolean;
    slack_oauth_url: string;
  } | null>(null);
  const [chatworkData, setChatworkData] = useState<{ id: any; is_enabled: boolean; token: any }>({
    id: "",
    is_enabled: false,
    token: "",
  });

  const closeModal = () => {
    setMode("default");
    setIsMenuOpen?.(true);
    changeModalCss?.("100%", "auto");
    setHeaderShow?.(true);
  };

  const openModal = (newMode: Mode) => {
    setMode(newMode);
    setIsMenuOpen?.(false);
    changeModalCss?.("600px", "auto");
    setHeaderShow?.(false);
  };

  const getNotificationSettings = async () => {
    const endpoint = `/${client.code}/settings/client_members/${client_member_id}/retrieve_notification`;

    const { data } = await axios.get(endpoint);
    setSlackData(data.slack_setting);
    setChatworkData(data.chatwork_setting);
    setLoading(false);
  };

  const openSlackAuthURL = () => {
    if (!slackData?.slack_oauth_url) return;
    window.open(slackData.slack_oauth_url, "SlackNotificationOauthWindow", "width=980,height=699");
  };

  const settingChatworkConnect = async () => {
    const endpoint = `/${client.code}/settings/client_member_chatwork_settings`;

    const params = {
      client_member_chatwork_setting: {
        client_member_id,
        chatwork_api_token: chatworkData.token,
      },
    };

    await axios
      .post(endpoint, params)
      .then((response) => {
        setChatworkData((prev) => ({
          ...prev,
          is_enabled: true,
          id: response.data.id,
        }));
        setNotice({
          message: response.data.message,
          type: "success",
        });
        closeModal();
      })
      .catch((error) => {
        if (error.response.data.message) {
          setNotice({
            message: error.response.data.message,
            type: "alert",
          });
        }
      });
  };

  const updateChatworkConnect = async () => {
    const endpoint = `/${client.code}/settings/client_member_chatwork_settings/${chatworkData.id}`;

    const params = {
      client_member_chatwork_setting: {
        chatwork_api_token: chatworkData.token,
      },
    };

    await axios
      .patch(endpoint, params)
      .then((response) => {
        setChatworkData((prev) => ({
          ...prev,
          is_enabled: true,
        }));
        setNotice({
          message: response.data.message,
          type: "success",
        });
        closeModal();
      })
      .catch((error) => {
        if (error.response.data.message) {
          setNotice({
            message: error.response.data.message,
            type: "alert",
          });
        }
      });
  };

  const deleteSlackSettings = async () => {
    if (!slackData?.id) return;
    const endpoint = `/${client.code}/settings/client_member_slack_settings/${slackData.id}`;

    const result = await axios.delete(endpoint);

    if (result?.data.message) {
      closeModal();
      setNotice({
        message: result.data.message,
        type: "success",
      });
      setSlackData({
        is_enabled: false,
        id: slackData.id,
        slack_oauth_url: slackData.slack_oauth_url,
      });
    }
  };

  useEffect(() => {
    getNotificationSettings();
  }, []);

  const renderContent = () => {
    switch (mode) {
      case "chatwork":
        return (
          <div className={cummonStyle.popup}>
            <h1 className={`${cummonStyle.popupTitle}`} data-testid="notifi-chatwork-setting-title">
              {t("NotificationSettings.Chatworkの通知設定")}
            </h1>
            <FormInputText
              label={t("NotificationSettings.APIトークン")}
              value={chatworkData.token}
              description={
                <span>
                  {t("NotificationSettings.トークン取得方法は")}
                  <a href={t("NotificationSettings.APIトークン取得方法URL")} target="_blank" rel="noopener noreferrer">
                    {t("NotificationSettings.こちら")}
                  </a>
                  {t("NotificationSettings.をご参照ください。")}
                </span>
              }
              onChange={(e) => {
                setChatworkData((prev) => ({ ...prev, token: e.target.value }));
              }}
              dataTestId="chatwork-api-token"
            />
            <div className={cummonStyle.buttonContent}>
              <button className={`btn btn-secondary`} onClick={closeModal}>
                {t("NotificationSettings.キャンセル")}
              </button>
              {chatworkData.is_enabled ? (
                <button className={`btn btn-info`} onClick={updateChatworkConnect}>
                  {t("NotificationSettings.更新する")}
                </button>
              ) : (
                <button className={`btn btn-info`} onClick={settingChatworkConnect} data-testid="chatwork-register-btn">
                  {t("NotificationSettings.登録する")}
                </button>
              )}
            </div>
          </div>
        );

      case "unlink":
        return (
          <div className={cummonStyle.popup}>
            <h1 className={`${cummonStyle.popupTitle}`}>{t("NotificationSettings.通知設定を解除しますか？")}</h1>
            <p className={cummonStyle.text}>{t("NotificationSettings.解除すると、各種通知が送信されなくなります。")}</p>
            <div className={cummonStyle.buttonContent}>
              <button className={`btn btn-secondary`} onClick={closeModal}>
                {t("NotificationSettings.キャンセル")}
              </button>
              <button className={`btn btn-info`} onClick={deleteSlackSettings}>
                {t("NotificationSettings.解除する")}
              </button>
            </div>
          </div>
        );

      default:
        return (
          <>
            <h1 className={cummonStyle.title} data-testid="notification-settings-title">
              {t("NotificationSettings.通知設定")}
            </h1>
            <p className={cummonStyle.description}>
              {t(
                "NotificationSettings.各種通知（フローボット完了・エラー・対応依頼・承認依頼・事前確認設定等）をメールだけではなく、SlackやChatworkで受け取ることが可能です。",
              )}
            </p>
            <div className={styles.notificationService}>
              <div className={styles.service}>
                <div className={styles.serviceTitle}>
                  <img src={service_icon.slack} alt="" width={47} height={47} />
                  <span>Slack</span>
                </div>
                {slackData?.is_enabled ? (
                  <button
                    className={`btn btn-secondary btn-sm-size ${styles.settedBtn}`}
                    onClick={() => openModal("unlink")}
                  >
                    <img src={getImageUrl("images/icons/checkGradation2.svg")} alt="" width={15} height={15} />
                    {t("NotificationSettings.設定済")}
                  </button>
                ) : (
                  <button
                    className={`btn btn-info btn-sm-size ${styles.button}`}
                    onClick={openSlackAuthURL}
                    data-testid="notifi-slack-setting-btn"
                  >
                    {t("NotificationSettings.設定する")}
                  </button>
                )}
              </div>
              <div className={styles.service}>
                <div className={styles.serviceTitle}>
                  <img src={service_icon.chatwork} alt="" width={47} height={47} />
                  <span>Chatwork</span>
                </div>
                {chatworkData.is_enabled ? (
                  <button
                    className={`btn btn-secondary btn-sm-size ${styles.settedBtn}`}
                    onClick={() => openModal("chatwork")}
                  >
                    <img src={getImageUrl("images/icons/checkGradation2.svg")} alt="" width={15} height={15} />
                    {t("NotificationSettings.設定済")}
                  </button>
                ) : (
                  <button
                    className={`btn btn-info btn-sm-size ${styles.button}`}
                    onClick={() => openModal("chatwork")}
                    data-testid="notifi-chatwork-setting-btn"
                  >
                    {t("NotificationSettings.設定する")}
                  </button>
                )}
              </div>
            </div>
          </>
        );
    }
  };

  return <>{!loading ? renderContent() : <PageLoader loadingHeight={"50vh"} />}</>;
};

export default NotificationSettings;
